import React from "react";
import { Alert } from "react-bootstrap";
import { FormattedMessage, MessageDescriptor } from "react-intl";
import { messages } from "../experiment.messages";

export interface ResultsWarningsBannerProps {
  /** `GetExperimentResultsResponse.partial` — undefined while the results query has not resolved. */
  partial: boolean | undefined;
  /** `GetExperimentResultsResponse.warnings` — raw wire codes, translated here. */
  warnings: string[] | undefined;
}

/**
 * Wire warning codes this screen knows how to explain. Anything else still reaches the user through
 * the `unknown` fallback, which echoes the raw code — a warning the front end has not been taught
 * about is still a warning worth showing, and the code is what makes it reportable.
 */
const KNOWN_WARNINGS: Record<string, MessageDescriptor> = {
  WAREHOUSE_UNAVAILABLE: messages.results.warnings.warehouseUnavailable,
  METRICS_UNAVAILABLE: messages.results.warnings.metricsUnavailable,
  MULTI_CURRENCY: messages.results.warnings.multiCurrency,
};

/**
 * Codes that describe the data rather than degrade it. They are still rendered on this screen —
 * DATA_UNTIL_YESTERDAY is the decision panel's footer note — but never inside a warning banner,
 * where a permanent, expected property of a nightly pipeline reads as something going wrong.
 * Filtered out here rather than left to the `unknown` fallback, which would echo the raw code.
 */
const NOT_A_DEGRADATION = new Set(["DATA_UNTIL_YESTERDAY"]);

/**
 * The page-level data-quality banner. Replaces the `console.warn`-only diagnostic the results shell
 * carried until now: a degraded fetch renders zeros and empty series that are indistinguishable
 * from real "nothing happened yet" data, so the user has to be told.
 *
 * Presentational only — the shell already holds the results payload it needs for the rest of the
 * header, so this component takes `partial`/`warnings` as props rather than opening a query of its
 * own.
 */
export function ResultsWarningsBanner({
  partial,
  warnings,
}: ResultsWarningsBannerProps): JSX.Element | null {
  // Only real degradations reach this banner now: a response whose sole warning is the nightly
  // cut-off renders nothing at all here, because that note lives in the decision panel instead.
  const codes = (warnings ?? []).filter(code => !NOT_A_DEGRADATION.has(code));
  if (!partial && codes.length === 0) {
    return null;
  }

  return (
    <Alert variant="warning" className="mb-0" data-testid="results-warnings-banner">
      <p className="font-weight-bold mb-1">
        <FormattedMessage {...messages.results.dataIncomplete} />
      </p>
      <ul className="mb-0 ps-3">
        {codes.map(code => (
          <li key={code} data-testid={`results-warning-${code}`}>
            {KNOWN_WARNINGS[code] ? (
              <FormattedMessage {...KNOWN_WARNINGS[code]} />
            ) : (
              <FormattedMessage {...messages.results.warnings.unknown} values={{ code }} />
            )}
          </li>
        ))}
      </ul>
    </Alert>
  );
}
